/**
 * Landing Page Shared Components
 * Common behaviors for all landing page components
 * Vanilla JavaScript (no Vue dependencies)
 */

/**
 * Initialize all shared component behaviors
 */
export function initComponents() {
  initAccordions();
  initTabs();
  initTooltips();
}

/**
 * Accordion functionality
 * Elements with data-accordion-trigger toggle data-accordion-content
 * Also handles native <details> elements with data-accordion-single-open attribute
 */
function initAccordions() {
  const triggers = document.querySelectorAll('[data-accordion-trigger]');

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const targetId = trigger.dataset.accordionTrigger;
      const content = document.querySelector(`[data-accordion-content="${targetId}"]`);

      if (content) {
        const isOpen = content.classList.contains('hidden');

        if (isOpen) {
          content.classList.remove('hidden');
          trigger.setAttribute('aria-expanded', 'true');
        } else {
          content.classList.add('hidden');
          trigger.setAttribute('aria-expanded', 'false');
        }
      }
    });
  });

  // Handle native <details> elements with single-open behavior
  const accordionGroups = document.querySelectorAll('[data-accordion-single-open]');

  accordionGroups.forEach(group => {
    const details = group.querySelectorAll('details');

    details.forEach(detail => {
      detail.addEventListener('toggle', () => {
        if (detail.open) {
          // Close all other details in the same group
          details.forEach(otherDetail => {
            if (otherDetail !== detail && otherDetail.open) {
              otherDetail.open = false;
            }
          });
        }
      });
    });
  });
}

/**
 * Tab functionality
 * Elements with data-tab-trigger show data-tab-content
 */
function initTabs() {
  const triggers = document.querySelectorAll('[data-tab-trigger]');

  triggers.forEach(trigger => {
    trigger.addEventListener('click', () => {
      const targetId = trigger.dataset.tabTrigger;
      const group = trigger.dataset.tabGroup || 'default';

      // Hide all tabs in this group
      const allTabs = document.querySelectorAll(`[data-tab-content][data-tab-group="${group}"]`);
      allTabs.forEach(tab => tab.classList.add('hidden'));

      // Remove active state from all triggers in this group
      const allTriggers = document.querySelectorAll(`[data-tab-trigger][data-tab-group="${group}"]`);
      allTriggers.forEach(t => t.classList.remove('active', 'border-b-2', 'border-purple-600'));

      // Show target tab
      const content = document.querySelector(`[data-tab-content="${targetId}"]`);
      if (content) {
        content.classList.remove('hidden');
      }

      // Mark trigger as active
      trigger.classList.add('active', 'border-b-2', 'border-purple-600');
    });
  });
}

/**
 * Tooltip functionality
 * Elements with data-tooltip show tooltip on hover
 */
function initTooltips() {
  const tooltipElements = document.querySelectorAll('[data-tooltip]');

  tooltipElements.forEach(el => {
    const tooltipText = el.dataset.tooltip;

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg opacity-0 pointer-events-none transition-opacity duration-200 -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap';
    tooltip.textContent = tooltipText;

    // Ensure parent has position context
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }

    el.appendChild(tooltip);

    // Show/hide on hover
    el.addEventListener('mouseenter', () => {
      tooltip.classList.remove('opacity-0');
      tooltip.classList.add('opacity-100');
    });

    el.addEventListener('mouseleave', () => {
      tooltip.classList.remove('opacity-100');
      tooltip.classList.add('opacity-0');
    });
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initComponents);
} else {
  initComponents();
}
